import { SetStateAction, useState, useContext, useEffect } from "react";
import { Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import ReactCodeInput from "react-code-input";
import { useNavigate } from "react-router-dom";

import * as Regex from 'utils/validations';
import { DataContext } from 'providers/data';
import { checkListPassword } from 'core/dataCheckListPassword';

import Modals from 'components/views/Modals';
import Collapse from '@mui/material/Collapse';
import Input from 'components/views/form/Input';
import { Typography, Box } from '@mui/material';
import ContentForgot from 'components/views/ContentForgot';
import { ButtonOutlined, ButtonRectangle } from 'components/views/form/Button';

import CircleIcon from '@mui/icons-material/Circle';
import { ReactComponent as IcIturinOk } from 'assets/icons/ic-iturin-ok.svg';
import { ReactComponent as IcIturinCoverage } from 'assets/icons/ic-iturin-coverage.svg';

import { ContentReactCode } from 'components/views/ContentForgot/styles';

import BackgroundForgotLogin from 'assets/images/background-forgot-login.png';

const ForgotPassword = () => {
    const navigate = useNavigate();
    const { register, watch } = useForm();
    const { dataUserAccountPassword, dataSendToken, dataValidatePasswordToken, dataUpdatePassword, handlePostValidateEmailLogin, handlePostSendToken, handlePostValidatePasswordToken, handlePostUpdatePassword, handleClearAuthentication, handlePostAuthentication } = useContext(DataContext);

    const [btnIsEmail, setBtnIsEmail] = useState(false);
    const [isLoadingBtn, setIsLoadingBtn] = useState(false);
    const [stepRecoveryLogin, setStepRecoveryLogin] = useState(0);
    const [enableBtnIdentity, setEnableBtnIdentity] = useState(false);
    const [showModalPasswordError, setShowModalPasswordError] = useState(false);

    const [emailContact, setEmailContact] = useState("");
    const [phoneContact, setPhoneContact] = useState("");

    const [pinCode, setPinCode] = useState("");
    const [isPinCodeValid, setIsPinCodeValid] = useState(true);
    const [remainingSeconds, setRemainingSeconds] = useState(60);

    const login = watch('login');
    const passwordNew = watch('passwordNew');
    const passwordNewConfirm = watch('passwordNewConfirm');

    const handlePostAuthenticationState = () => {
        setIsLoadingBtn(true);
        handlePostAuthentication({ usuario: login, password: passwordNew });
    }

    const handlePinChange = (pinCode: SetStateAction<string>) => {
        setPinCode(pinCode);
    }

    const handlePostValidateEmailLoginState = () => {
        setIsLoadingBtn(true);
        login && handlePostValidateEmailLogin(login);
    }

    const handlePostSendTokenState = (isEmail: boolean) => {
        setBtnIsEmail(isEmail);
        setIsLoadingBtn(true);

        if (dataUserAccountPassword) {
            handlePostSendToken({
                CdPerson: dataUserAccountPassword.cdPerson,
                ContatoClienteEnum: 1,
                PhoneId: isEmail ? 0 : dataUserAccountPassword.cdPersonPhone,
                SendToEmail: isEmail
            });
        }
    }

    const handlePostValidatePasswordTokenState = () => {
        setIsLoadingBtn(true);

        if (dataUserAccountPassword) {
            handlePostValidatePasswordToken({
                CdPerson: dataUserAccountPassword.cdPerson,
                Token: pinCode,
            });
        }
    }

    const handlePostUpdatePasswordState = () => {
        setIsLoadingBtn(true);

        if (dataUserAccountPassword) {
            handlePostUpdatePassword({
                PersonId: dataUserAccountPassword.cdPerson,
                Token: pinCode,
                password: passwordNew,
                confirmPassword: passwordNewConfirm
            });
        }
    }

    const handleValidateRequirementsPassword = (index: number) => {

        switch (index) {
            case 0:
                if ((passwordNew?.length > 5) && (passwordNew?.length < 16)) {
                    return 'secondary.main';
                } else {
                    return 'error.light';
                }

            case 1:
                if (passwordNew?.match(/[A-Z]/)) {
                    return 'secondary.main';
                } else {
                    return 'error.light';
                }

            case 2:
                if (passwordNew?.match(/[a-z]/)) {
                    return 'secondary.main';
                } else {
                    return 'error.light';
                }

            case 3:
                if (passwordNew?.match(/[0-9]/)) {
                    return 'secondary.main';
                } else {
                    return 'error.light';
                }

            case 4:
                if (passwordNew?.match(/\W|_/) && !passwordNew?.match(Regex.emoji)) {
                    return 'secondary.main';
                } else {
                    return 'error.light';
                }
        }
    }

    // Contator regressivo
    useEffect(() => {
        let timer: NodeJS.Timeout;
        if ((remainingSeconds > 0) && (stepRecoveryLogin === 3)) {
            timer = setInterval(() => {
                setRemainingSeconds((seconds) => seconds - 1);
            }, 1000);

        } else if (remainingSeconds === 0) {
            setStepRecoveryLogin(2);
            setRemainingSeconds(60);
        }

        return () => clearInterval(timer);
    }, [remainingSeconds, stepRecoveryLogin]);

    const formatTimeSeconds = (time: number) => {
        return time.toString().padStart(2, '0');
    }

    useEffect(() => {
        if (dataUserAccountPassword && typeof dataUserAccountPassword === 'object') {
            setStepRecoveryLogin(2);
            dataUserAccountPassword.email && setEmailContact(dataUserAccountPassword.email);
            dataUserAccountPassword?.phoneNumber && setPhoneContact(`(${dataUserAccountPassword.phoneDDD}) ${dataUserAccountPassword.phoneNumber.substring(0, 5)}-${dataUserAccountPassword.phoneNumber.substring(5, 9)}`);
        } else if (dataUserAccountPassword === false) {
            setStepRecoveryLogin(1);
        }
        setIsLoadingBtn(false);
    }, [dataUserAccountPassword]);

    useEffect(() => {
        if (dataSendToken) {
            setStepRecoveryLogin(3);
        } else if (dataSendToken === false) {
            setStepRecoveryLogin(1);
        }
        setIsLoadingBtn(false);
    }, [dataSendToken]);

    useEffect(() => {
        if (stepRecoveryLogin === 0) {
            if (login) {
                setEnableBtnIdentity(false);
            } else {
                setEnableBtnIdentity(true);
            }
        } else if (stepRecoveryLogin === 4) {
            if ((passwordNew === passwordNewConfirm) && (passwordNew?.length > 5) && (passwordNew?.length < 16) && passwordNew?.match(/[A-Z]/) && passwordNew?.match(/[a-z]/) && passwordNew?.match(/[0-9]/) && passwordNew?.match(/\W|_/) && !passwordNew?.match(Regex.emoji)) {
                setEnableBtnIdentity(false);
            } else {
                setEnableBtnIdentity(true);
            }
        }
    }, [login, stepRecoveryLogin, passwordNew, passwordNewConfirm]);

    useEffect(() => {
        if (dataValidatePasswordToken) {
            setIsPinCodeValid(true);
            setStepRecoveryLogin(4);
        } else if (dataValidatePasswordToken === false) {
            setIsPinCodeValid(false);
        }
        setIsLoadingBtn(false);
    }, [dataValidatePasswordToken]);

    useEffect(() => {
        if (dataUpdatePassword) {
            setStepRecoveryLogin(5);
        } else if (dataUpdatePassword === false) {
            setShowModalPasswordError(true);
        }
        setIsLoadingBtn(false);
    }, [dataUpdatePassword]);

    return (
        <ContentForgot background={BackgroundForgotLogin} titleNav='Esqueci minha senha'>
            <Box sx={{
                height: '100%',
                display: 'flex',
                marginTop: '-3rem',
                flexDirection: 'column',
                justifyContent: 'center'
            }}>
                {(stepRecoveryLogin !== 1) && (stepRecoveryLogin !== 5) &&
                    <Box sx={{
                        width: {
                            xs: '100%',
                            md: '25rem'
                        },
                        display: 'flex',
                        margin: '0 auto',
                        textAlign: 'center',
                        borderRadius: '10px',
                        alignItems: 'center',
                        flexDirection: 'column',
                        padding: {
                            xs: '2.375rem 1rem',
                            md: '2.375rem 1.5rem'
                        },
                        border: {
                            xs: 'none',
                            md: '1.5px solid rgba(12, 28, 51, 0.5)'
                        },
                        'button': {
                            width: '100%',
                            marginTop: '.6875rem'
                        }
                    }}>
                        <Typography fontSize='1.5625rem' fontWeight={700} sx={{ display: { xs: 'none', md: 'flex' } }}>
                            Esqueci minha senha
                        </Typography>

                        <Collapse in={stepRecoveryLogin !== 5} sx={{ width: '100%' }}>
                            <>
                                <Typography fontSize='.875rem' lineHeight='1rem' fontWeight={400} sx={{
                                    maxWidth: '22.81rem',
                                    padding: {
                                        xs: '0 0 3.25rem',
                                        md: '1rem 0 1.875rem'
                                    }
                                }}>
                                    {stepRecoveryLogin === 0 &&
                                        'Para informar sua senha, preencha o campo abaixo.'
                                    }

                                    {stepRecoveryLogin === 2 &&
                                        <>Encontramos seus dados, <strong>escolha</strong> como gostaria de receber seu token:</>
                                    }

                                    {stepRecoveryLogin === 3 &&
                                        <><strong style={{ fontSize: '1.25rem', fontWeight: 400 }}>Quase lá!</strong><br />Insira o código recebido:</>
                                    }
                                    {stepRecoveryLogin === 4 &&
                                        <>Crie a sua<strong> nova senha </strong>para ter acesso ao <br /> mundo Ituran.</>
                                    }
                                </Typography>

                                <Collapse in={stepRecoveryLogin === 0}>
                                    <Input
                                        name='login'
                                        label='Insira seu login'
                                        placeholder='Seu login'
                                        type='text'
                                        register={{ ...register("login", { required: true }) }} />
                                </Collapse>

                                <Collapse in={stepRecoveryLogin === 2}>
                                    <Box sx={{ textAlign: 'start', display: 'flex', flexDirection: 'column', alignItems: 'center', 'button': { marginTop: '.3rem', height: '2.8175rem' } }}>
                                        {phoneContact &&
                                            <>
                                                <Box sx={{ width: '100%', maxWidth: '20.625rem' }}>
                                                    <Typography fontSize='.75rem' fontWeight={400} >
                                                        SMS
                                                    </Typography>
                                                    <ButtonRectangle name={phoneContact} onClick={() => handlePostSendTokenState(false)} loadingBtn={isLoadingBtn && !btnIsEmail} />
                                                </Box>
                                                <Box
                                                    sx={{
                                                        width: '100%',
                                                        maxWidth: '20.625rem',
                                                        display: 'flex',
                                                        gap: '1.1875rem',
                                                        alignItems: 'center',
                                                        'div': {
                                                            width: '100%',
                                                            height: '1px',
                                                            background: 'rgba(28, 56, 107, 0.4)',
                                                        },
                                                        'p': {
                                                            display: 'flex',
                                                            color: '#A4AFC4',
                                                            padding: '1.25rem 0'
                                                        }
                                                    }}>
                                                    <div />
                                                    <p>ou</p>
                                                    <div />
                                                </Box>
                                            </>
                                        }
                                        <Box sx={{ width: '100%', maxWidth: '20.625rem' }}>
                                            <Typography fontSize='.75rem' fontWeight={400} >
                                                E-mail
                                            </Typography>
                                            <ButtonOutlined name={emailContact} onClick={() => handlePostSendTokenState(true)} loadingBtn={isLoadingBtn && btnIsEmail} />
                                        </Box>
                                    </Box>
                                </Collapse>

                                <Collapse in={stepRecoveryLogin === 3}>
                                    <ContentReactCode invalidPin={!isPinCodeValid ? 'error' : 'success'}>
                                        <Typography variant="body2" color='error.main' textAlign='start' paddingBottom='.5rem' sx={{ height: '20px' }}>
                                            {!isPinCodeValid && 'Código inválido, por favor tente novamente.'}
                                        </Typography>

                                        <ReactCodeInput
                                            type='number'
                                            fields={6}
                                            name=""
                                            inputMode="tel"
                                            onChange={handlePinChange}
                                            value={pinCode}
                                            inputStyle={{
                                                width: '3.125rem',
                                                height: '3.125rem',
                                                borderRadius: '5px',
                                                fontSize: '1.875rem',
                                                textAlign: 'center',
                                                paddingTop: '5px'
                                            }}
                                        />
                                    </ContentReactCode>
                                </Collapse>

                                <Collapse in={stepRecoveryLogin === 4} sx={{ marginTop: '-1rem', 'button': { margin: '0' } }}>
                                    <Input name="passwordNew" label='Nova Senha' placeholder='***********' type='password' register={{ ...register("passwordNew", { required: true }) }} />
                                    <Input name="passwordNewConfirm" label='Confirmar nova senha' placeholder='***********' type='password' register={{ ...register("passwordNewConfirm", { required: true }) }} />

                                    <ul>
                                        {checkListPassword.map((value, index) => (
                                            <li key={index}>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        maxWidth: '21rem',
                                                        marginBottom: '1rem',
                                                        'svg': {
                                                            width: '.5rem',
                                                            height: '.5rem',
                                                            margin: '1px .3rem 0 0',
                                                            'path': {
                                                                color: handleValidateRequirementsPassword(index)
                                                            }
                                                        }
                                                    }}>
                                                    <CircleIcon />
                                                    <Typography variant="body2" color='#ABAEAE' textAlign='start' lineHeight='.875rem'>
                                                        {value}
                                                    </Typography>
                                                </Box>
                                            </li>
                                        ))}
                                    </ul>
                                </Collapse>
                            </>
                        </Collapse>

                        <Collapse in={stepRecoveryLogin !== 2} sx={{ width: '100%', 'button': { marginTop: '1rem', height: '2.5rem' } }}>
                            <ButtonRectangle
                                name={stepRecoveryLogin === 0 ? "RECUPERAR" :
                                    stepRecoveryLogin === 3 ? "VALIDAR CÓDIGO" : 'SALVAR SENHA'}
                                disabled={enableBtnIdentity}
                                loadingBtn={isLoadingBtn}
                                onClick={() =>
                                    stepRecoveryLogin === 0 ? handlePostValidateEmailLoginState() :
                                        stepRecoveryLogin === 3 ? handlePostValidatePasswordTokenState() : handlePostUpdatePasswordState()}
                            />
                            <Collapse in={stepRecoveryLogin === 3}>
                                <ButtonOutlined name="Cancelar" onClick={() => setStepRecoveryLogin(2)} />

                                <Typography variant='subtitle2' color='#ABAEAE' sx={{ paddingTop: { xs: '1.1875rem', md: '3.375rem' } }}>
                                    Reenvio do token em 00:{formatTimeSeconds(remainingSeconds)}
                                </Typography>
                            </Collapse>
                        </Collapse>

                        <Collapse in={stepRecoveryLogin === 0}>
                            <Link
                                to='/esqueci-login'
                                style={{
                                    display: 'flex',
                                    width: 'fit-content',
                                    margin: '2.75rem 0 .5rem'
                                }}>
                                <Typography variant='subtitle2' sx={{ textDecoration: 'underline' }}>
                                    Esqueci meu login
                                </Typography>
                            </Link>
                        </Collapse>
                    </Box>
                }

                {
                    stepRecoveryLogin === 1 &&
                    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                        <IcIturinCoverage />
                        <Box sx={{
                            border: '1.5px solid rgba(10, 168, 227, 0.45)',
                            borderRadius: '10px',
                            maxWidth: '21.875rem',
                            padding: '1.5625rem 1.25rem 1.875rem',
                            marginTop: '-30px',
                            backgroundColor: 'white',
                            'button': {
                                width: '100%',
                                height: '2.5rem',
                                marginTop: '.875rem'
                            }
                        }}>
                            <Typography fontSize='1.5625rem' textAlign='center' fontWeight={700}>
                                Login não localizado.
                            </Typography>
                            <Typography fontSize='1rem' textAlign='center' marginTop='1.875rem'>
                                Ainda não é cadastrado?
                            </Typography>
                            <ButtonRectangle name="1º ACESSO" onClick={() => { navigate('/primeiro-acesso'); handleClearAuthentication(); }} />
                            <ButtonOutlined
                                name="TENTAR NOVAMENTE"
                                onClick={() => setStepRecoveryLogin(0)} />
                        </Box>
                    </Box>
                }

                <Collapse in={stepRecoveryLogin === 0 || stepRecoveryLogin === 2} sx={{ display: { xs: 'block', md: 'none' } }}>
                    <Box sx={{
                        paddingTop: '3.25rem',
                        textAlign: 'center',
                        display: 'flex',
                        alignItems: 'center',
                        flexDirection: 'column',
                        justifyContent: 'center'
                    }}>
                        <Box sx={{ width: '100%', borderTop: '1px solid rgba(28, 56, 107, 0.4)', marginBottom: '3.375rem', maxWidth: '200px' }} />
                        <Link to='/primeiro-acesso'>
                            <Typography fontWeight={600} sx={{
                                fontSize: '.875rem',
                                color: '#ABAEAE',
                                'strong': {
                                    color: 'primary.main',
                                    textDecoration: 'underline'
                                }
                            }}>
                                Ainda não é cadastrado? <strong>1º Acesso</strong>
                            </Typography>
                        </Link>
                    </Box>
                </Collapse>

                <Collapse in={[2].indexOf(stepRecoveryLogin) >= 0}>
                    <Box sx={{ textAlign: 'center', 'button': { marginTop: '2rem', maxWidth: '100px' } }}>
                        <ButtonOutlined
                            name='Voltar'
                            onClick={() => setStepRecoveryLogin(0)}
                        />
                    </Box>
                </Collapse>

                {stepRecoveryLogin === 5 &&
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        maxWidth: '20.68rem',
                        margin: '0 auto',
                        'button': {
                            width: '100%',
                            height: '2.5rem',
                            marginTop: '2.25rem'
                        }
                    }}>
                        <IcIturinOk />
                        <Box sx={{
                            border: '1.5px solid rgba(151, 201, 61, 0.45)',
                            borderRadius: '10px',
                            height: '3.125rem',
                            padding: '.8rem 5.1rem 0',
                            marginTop: '-34px',
                            backgroundColor: 'white'
                        }}>
                            <Typography fontSize='1.5625rem' textAlign='center' fontWeight={700}>
                                Dados salvos!
                            </Typography>
                        </Box>
                        <ButtonRectangle name="ACESSAR" loadingBtn={isLoadingBtn} onClick={() => { handleClearAuthentication(); handlePostAuthenticationState(); }} />
                    </Box>
                }

                <Modals open={showModalPasswordError}>
                    <Box sx={{ textAlign: 'center', borderRadius: '8px', width: { xs: 'calc(100vw - 2rem)', md: '20rem' } }}>
                        <Box sx={{ borderRadius: '8px 8px 0 0' }} padding='1.625rem 1.375rem 1.25rem'>
                            <Typography fontSize='1rem' fontWeight={700} lineHeight='20px'>
                                A nova senha não pode ser igual a senha atual.
                            </Typography>
                        </Box>

                        <Box sx={{
                            backgroundColor: 'primary.main',
                            borderRadius: '0 0 8px 8px',
                            padding: {
                                xs: '1rem 2.5rem 1.625rem',
                                md: '1.875rem 2rem 1.625rem'
                            },
                            'button': {
                                width: '12.5rem',
                                height: '2.5rem'
                            }
                        }}>
                            <ButtonRectangle name='Fechar' onClick={() => setShowModalPasswordError(false)} />
                        </Box>
                    </Box>
                </Modals>
            </Box>
        </ContentForgot>
    );
}

export default ForgotPassword;